import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Calculator, TrendingUp, DollarSign, BarChart2, Percent, FileSpreadsheet, ArrowRight, Wrench } from 'lucide-react';

const tools = [
  { name: 'GST Calculator', desc: 'Calculate CGST, SGST instantly', icon: Percent, path: '/tools/gst-calculator', color: 'from-blue-500 to-cyan-400' },
  { name: 'EMI Calculator', desc: 'Plan your loan repayments', icon: Calculator, path: '/tools/emi-calculator', color: 'from-purple-500 to-pink-400' },
  { name: 'Profit & Margin', desc: 'Analyze business profitability', icon: TrendingUp, path: '/tools/profit-calculator', color: 'from-green-500 to-emerald-400' },
  { name: 'Break-Even Tool', desc: 'Find your break-even point', icon: BarChart2, path: '/tools/break-even-analysis', color: 'from-amber-500 to-orange-400' },
  { name: 'Invoice Generator', desc: 'Professional GST invoices', icon: FileSpreadsheet, path: '/tools/invoice-generator', color: 'from-indigo-500 to-violet-400' },
  { name: 'Loan Eligibility', desc: 'Check your loan limit instantly', icon: DollarSign, path: '/loan-eligibility', color: 'from-rose-500 to-red-400' },
];

export function ToolsPreview() {
  const navigate = useNavigate();

  return (
    <section className="py-24 bg-white dark:bg-[#0A1628] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-600/5 rounded-full blur-[100px]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 text-sm font-bold mb-6">
            <Wrench className="w-4 h-4" /> Free Business Tools
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Smart Tools for Smarter Business
          </h2>
          <p className="text-slate-900 dark:text-slate-400 mt-4 text-lg max-w-xl mx-auto">
            Free, instant calculators built for Indian MSMEs. No signup needed.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {tools.map((tool, i) => (
            <motion.div
              key={tool.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              whileHover={{ y: -8, scale: 1.05 }}
              onClick={() => navigate(tool.path)}
              className="group cursor-pointer backdrop-blur-xl bg-white dark:bg-[#1A2A4A] border border-slate-200 dark:border-[#1E2A3A] shadow-sm dark:shadow-none rounded-2xl p-5 hover:border-white/25 hover:bg-gray-200 dark:bg-[#111C2F] transition-all duration-300 text-center shadow-xl"
            >
              <div className={`w-12 h-12 mx-auto rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center mb-4 shadow-lg group-hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] transition-all duration-300 group-hover:scale-110`}>
                <tool.icon className="w-6 h-6 text-slate-900 dark:text-white" />
              </div>
              <div className="text-slate-900 dark:text-white font-bold text-sm leading-tight mb-1">{tool.name}</div>
              <div className="text-slate-900 dark:text-slate-400 text-xs leading-tight">{tool.desc}</div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={() => navigate('/tools')}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full border border-gray-300 dark:border-[#2A3F5F] text-gray-700 dark:text-white/70 hover:text-slate-900 dark:text-white hover:border-white/40 text-sm font-bold transition-all"
          >
            Explore All 54 Tools <ArrowRight className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </section>
  );
}
